/**
 * @file: AdvancedImage.shared.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { StyleProps } from '@euler/components/layout/View';
import { Size } from '@euler/model/common';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { StyleProp } from 'react-native';
import { FastImageProps, ImageStyle, Source } from 'react-native-fast-image';
import { imageResizing } from './image-resizing';

const loadedImageUrls: { [url: string]: boolean } = {};

export type AdvancedImageProps = Omit<FastImageProps, 'source' | 'onLoad'> & {
  uri?: string | null;
  source?: Source;
  resize?: imageResizing.Options;
  placeholder?: ReactNode;
  style?: StyleProp<ImageStyle>;
  onLoad?: (size: Size) => void;
  blurhash?:
    | string
    | {
        blurhash: string;
        decodeAsync?: boolean;
        decodeWidth?: number;
        decodeHeight?: number;
      };
} & StyleProps;

export const useImage = ({
  uri,
  resize,
  blurhash,
  onLoad,
  imageDimensFromLoadEvent,
}: Pick<AdvancedImageProps, 'uri' | 'resize' | 'blurhash' | 'onLoad'> & {
  imageDimensFromLoadEvent: (e: any, uri: string) => Promise<Size> | Size;
}) => {
  const hash = useMemo(() => {
    try {
      const value =
        blurhash ??
        (uri?.startsWith('http')
          ? decodeURIComponent(uri.split('#')[1] ?? '')
          : '');
      if (typeof value === 'string' && value.length < 6) return '';
      return value;
    } catch (e) {
      return '';
    }
  }, [uri, blurhash]);

  const imageUrl = useMemo(
    () => (resize ? imageResizing.imageUriResized(uri, resize)! : uri),
    [uri, resize],
  );

  const urlRef = useRef(imageUrl);
  urlRef.current = imageUrl;

  const [showBlur, setShowBlur] = useState(
    Boolean(hash && imageUrl && !loadedImageUrls[imageUrl!]),
  );

  useEffect(() => {
    setShowBlur(Boolean(hash && imageUrl && !loadedImageUrls[imageUrl!]));
  }, [hash, imageUrl]);

  const onLoaded = usePersistFn((event: any) => {
    setShowBlur(false);
    if (urlRef.current) {
      loadedImageUrls[urlRef.current] = true;
    }
    if (onLoad != null && imageUrl != null) {
      const res = imageDimensFromLoadEvent(event, imageUrl);
      if (res instanceof Promise) {
        void res.then(onLoad).catch(onErrorIgnore);
      } else {
        onLoad(res);
      }
    }
  });

  return {
    hash,
    showBlur,
    imageUrl,
    onLoaded,
  };
};
